/**
 * =============================================================================
 * WlAddonsTab
 *
 * Conteúdo da aba "Add-ons" do painel WL Financeiro. Compõe:
 *   - WlAddonKpiGrid                 (4 KPIs premium)
 *   - WlAddonRevenueTrendChart       (Evolução do MRR de add-ons — 12 meses)
 *   - WlAddonCountsTrendChart        (Novos vs. Cancelados — 12 meses)
 *   - WlAddonsListCard               (Listagem paginada padronizada)
 *
 * Uma única chamada à RPC `wl_get_addon_kpis` alimenta KPIs + gráficos, e a
 * listagem consome `wl_list_tenant_addons` v2.0.0 (mesma fonte de verdade).
 *
 * @version 1.1.0
 * RELEASE:
 *   2026-07-22 v1.0.0 - Versão inicial (substitui bloco legado da aba).
 *   2026-07-22 v1.1.0 - Movidos "Distribuição do MRR por add-on" e "Receita
 *                       por add-on" da aba Visão geral para cá (linha
 *                       intermediária, entre gráficos e listagem).
 * =============================================================================
 */
import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Users } from 'lucide-react';
import { useWlAddonKpis } from '@/hooks/wl/useWlAddonKpis';
import { WlAddonKpiGrid } from './WlAddonKpiGrid';
import { WlAddonsListCard } from './WlAddonsListCard';
import { WlAddonRevenueTrendChart } from './charts/WlAddonRevenueTrendChart';
import { WlAddonCountsTrendChart } from './charts/WlAddonCountsTrendChart';
import { WlRevenueByAddonPieChart } from './charts/WlRevenueByAddonPieChart';
import { WlFinancialDetailModal } from './WlFinancialDetailModal';
import { ChartTypeToggle, ChartTypeMode } from './charts/ChartTypeToggle';
import { Skeleton } from '@/components/ui/skeleton';

/** Formatador BRL local (evita import adicional). */
const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(v || 0),
  );

interface Props {
  /** Mês de referência (yyyy-MM-dd) vindo do header global. */
  referenceMonth?: string | null;
}

export function WlAddonsTab({ referenceMonth }: Props) {
  const { data, isLoading } = useWlAddonKpis(referenceMonth ?? null);

  // Toggle bar/line controlado externamente (mesma UX da Visão geral).
  const [revenueMode, setRevenueMode] = useState<ChartTypeMode>('line');
  const [countsMode, setCountsMode] = useState<ChartTypeMode>('line');

  // Estado do modal de drill-down por linha (Receita por add-on).
  const [modal, setModal] = useState<{
    open: boolean;
    title: string;
    addonId: string | null;
  }>({ open: false, title: '', addonId: null });

  const byAddon = data?.by_addon ?? [];

  return (
    <div className="space-y-4">
      {/* KPIs premium */}
      <WlAddonKpiGrid referenceMonth={referenceMonth} />

      {/* Grade de gráficos evolutivos (2 colunas em desktop). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Evolução do MRR de Add-ons (12 meses)
            </CardTitle>
            <ChartTypeToggle value={revenueMode} onChange={setRevenueMode} />
          </CardHeader>
          <CardContent>
            {isLoading || !data ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <WlAddonRevenueTrendChart
                data={data.trend_revenue}
                mode={revenueMode}
                onModeChange={setRevenueMode}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              Novos Add-ons vs. Cancelados (12 meses)
            </CardTitle>
            <ChartTypeToggle value={countsMode} onChange={setCountsMode} />
          </CardHeader>
          <CardContent>
            {isLoading || !data ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <WlAddonCountsTrendChart
                data={data.trend_counts}
                mode={countsMode}
                onModeChange={setCountsMode}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/*
        ==================== Distribuição + Receita por add-on ====================
        Movidos da aba Visão geral (v1.7.0 do overview). Mesma fonte de verdade
        (data.by_addon) que alimentava o overview — top 20 add-ons por MRR.
      */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* --- Esquerda: Distribuição do MRR por add-on (donut) ------------- */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Distribuição do MRR por add-on
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Carregando…</p>
            ) : (
              <WlRevenueByAddonPieChart data={byAddon} />
            )}
          </CardContent>
        </Card>

        {/* --- Direita: Receita por add-on (tabela clicável) ---------------- */}
        <Card className="flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Receita por add-on</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[300px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead className="text-xs font-medium">Add-on</TableHead>
                    <TableHead className="text-xs font-medium text-center">Qtd</TableHead>
                    <TableHead className="text-xs font-medium text-right">MRR</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byAddon.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="text-center py-6 text-sm text-muted-foreground"
                      >
                        Sem dados
                      </TableCell>
                    </TableRow>
                  ) : (
                    byAddon.map((row) => (
                      <TableRow
                        key={row.addon_id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() =>
                          setModal({
                            open: true,
                            title: `Add-on: ${row.addon_name}`,
                            addonId: row.addon_id,
                          })
                        }
                      >
                        <TableCell className="py-2 text-xs font-medium">
                          <div>{row.addon_name}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {row.addon_slug}
                          </div>
                        </TableCell>
                        <TableCell className="py-2 text-xs text-center">
                          {row.quantity}
                        </TableCell>
                        <TableCell className="py-2 text-xs text-right">
                          {fmtBRL(row.mrr)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Listagem paginada com pills + busca + coluna Dt. criação. */}
      <WlAddonsListCard referenceMonth={referenceMonth} />

      {/* Drill-down por linha da tabela "Receita por add-on". */}
      <WlFinancialDetailModal
        open={modal.open}
        onOpenChange={(open) =>
          setModal((m) => ({ ...m, open }))
        }
        type="addon"
        title={modal.title}
        addonId={modal.addonId}
      />
    </div>
  );
}

